import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";

interface UseAsyncOperationOptions {
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Custom hook for handling async operations with loading states and error handling
 * Standardizes async operation patterns across the application
 */
export const useAsyncOperation = (options: UseAsyncOperationOptions = {}) => {
  const {
    successMessage,
    errorMessage = "An error occurred",
    onSuccess,
    onError,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operationCountRef = useRef(0);
  const isMountedRef = useRef(true);

  const execute = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T | null> => {
      // Increment operation count and set loading state
      operationCountRef.current++;
      if (!isMountedRef.current) return null;

      setIsLoading(true);
      setError(null);

      try {
        const result = await operation();

        // Only proceed if component is still mounted
        if (!isMountedRef.current) return null;

        // Show success toast if message provided
        if (successMessage) {
          const desc = navigator.onLine
            ? undefined
            : "Will sync when you reconnect";
          if (desc) {
            toast.success(successMessage, { description: desc });
          } else {
            toast.success(successMessage);
          }
        }

        onSuccess?.();
        return result;
      } catch (err) {
        // Only proceed if component is still mounted
        if (!isMountedRef.current) return null;

        let actualErrorMessage: string;
        let toastDescription: string;

        if (err instanceof Error) {
          actualErrorMessage = err.message || "An unexpected error occurred";
          toastDescription = err.message || "An unexpected error occurred";
        } else if (typeof err === "string") {
          actualErrorMessage = err;
          toastDescription = err;
        } else {
          actualErrorMessage = "An unexpected error occurred";
          toastDescription = "An unexpected error occurred";
        }

        setError(actualErrorMessage);

        // Show error toast
        toast.error(errorMessage, { description: toastDescription });

        // Only log to console in non-test environments to avoid stderr pollution
        if (process.env.NODE_ENV !== "test") {
          console.error("Async operation failed:", err);
        }
        onError?.(err);
        return null;
      } finally {
        // Decrement operation count and only set loading to false if no operations are running
        operationCountRef.current--;
        if (operationCountRef.current <= 0 && isMountedRef.current) {
          operationCountRef.current = 0; // Ensure it doesn't go negative
          setIsLoading(false);
        }
      }
    },
    [successMessage, errorMessage, onSuccess, onError],
  );

  const reset = useCallback(() => {
    operationCountRef.current = 0;
    setIsLoading(false);
    setError(null);
  }, []);

  // Cleanup function to prevent state updates after unmount
  const cleanup = useCallback(() => {
    isMountedRef.current = false;
  }, []);

  return {
    isLoading,
    error,
    execute,
    reset,
    cleanup,
  };
};
